import './styles.css';
import { selectVisualTier } from './visual/capability-policy.js';
import { fetchRuntimeVisualFlag } from './visual/runtime-flag.js';
import { shouldLoadVisual } from './visual/visual-bootstrap.js';

document.documentElement.classList.add('js-motion');

// Progressive-enhancement reveal. Content is fully visible without JS;
// this only adds the .in class that fades sections in once js-motion
// gates the initial opacity (see styles.css). Ported from the pre-Vite
// inline script so the build-boundary move doesn't hide content.
const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const revealEls = document.querySelectorAll('[data-reveal]');
if (reduceMotion || !('IntersectionObserver' in window)) {
  revealEls.forEach((el) => el.classList.add('in'));
} else {
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in');
        io.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });
  revealEls.forEach((el) => io.observe(el));
}

// Site-level "Reduce visual effects" preference. This is a user-facing
// control that COMPLEMENTS prefers-reduced-motion — it never overrides or
// weakens the OS preference (selectVisualTier ORs both into the same POSTER
// fallback; reducedMotion below is read independently and is never derived
// from this toggle). Synced before capability selection so a reload always
// applies the safe tier from the earliest bootstrap point.
const effectsToggle = document.querySelector('#effects-toggle');
const reducedEffects = localStorage.getItem('quantara:reduce-effects') === 'true';
if (reducedEffects) document.documentElement.classList.add('reduce-effects');
effectsToggle.setAttribute('aria-pressed', String(reducedEffects));
effectsToggle.addEventListener('click', () => {
  const next = effectsToggle.getAttribute('aria-pressed') !== 'true';
  localStorage.setItem('quantara:reduce-effects', String(next));
  location.reload();
});

// Clay video loops. All are decorative, muted, `preload="none"` — zero video
// bytes are fetched until the section approaches the viewport, and none at
// all under prefers-reduced-motion or the site effects toggle (the poster
// image stands in). play() on a preload="none" video triggers its load, so
// gating play IS gating the network cost. Pausing off-screen keeps the
// browser from burning cycles on loops nobody can see.
const clayVideos = document.querySelectorAll('video.loop-video');
if (clayVideos.length && !reduceMotion && !reducedEffects && 'IntersectionObserver' in window) {
  const vio = new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (isIntersecting) target.play().catch(() => {}); // autoplay veto → poster stays
      else target.pause();
    });
  }, { rootMargin: '220px 0px' });
  clayVideos.forEach((v) => vio.observe(v));
}

// Cinematic hero visual. `selectVisualTier` is cheap and dependency-free
// (no `three` import), so it runs synchronously here to decide whether the
// renderer is worth loading at all. Poster tier needs none of it: the
// renderer module (and the ~150KB+ `three` it pulls in) is only ever
// dynamic-imported, and only for tiers that will actually animate.
const poster = document.querySelector('#visual-poster');
const capabilities = {
  enabled: import.meta.env.VITE_LANDING_VISUALS !== 'false',
  webgl: Boolean(document.createElement('canvas').getContext('webgl2')),
  reducedMotion: reduceMotion,
  reduceEffects: reducedEffects,
  saveData: navigator.connection?.saveData === true,
  lowPower: (navigator.deviceMemory || 8) <= 2,
  mobile: window.matchMedia('(max-width: 900px), (pointer: coarse)').matches,
  deviceMemory: navigator.deviceMemory || 8,
  hardwareConcurrency: navigator.hardwareConcurrency || 8,
};

const tier = selectVisualTier(capabilities);
if (tier.id === 'poster') {
  // Genuine capability/build-time decision (no WebGL, prefers-reduced-motion,
  // low power, or a deliberate VITE_LANDING_VISUALS build-time disable) —
  // final, and never revisited by the runtime flag below. See
  // visual-bootstrap.js for why this precedence lives in one place.
  poster.hidden = false;
} else {
  const requestIdle = window.requestIdleCallback || ((callback) => setTimeout(callback, 0));
  requestIdle(async () => {
    // Instant server/config kill switch (rollout §12.7): shouldLoadVisual
    // reads it at REQUEST time from a same-origin, uncached endpoint so 3D
    // can be disabled without a rebuild/redeploy. This check happens BEFORE
    // the dynamic import below, so a disabled switch never even requests the
    // Three.js chunk. It runs inside the already-deferred idle callback, and
    // the poster is visible by default (see styles.css), so it never delays
    // or blocks first paint of the semantic content either way. Any fetch
    // failure, timeout, 404, or malformed body fails SAFE to disabled - see
    // runtime-flag.js and netlify/edge-functions/visual-config.js.
    const shouldLoad = await shouldLoadVisual(capabilities, {
      tier,
      fetchRuntimeVisualFlag,
      fetchImpl: typeof fetch === 'function' ? fetch : undefined,
    });
    if (!shouldLoad) {
      // The poster is normally visible from first paint. Set this explicitly
      // as well so a future markup or lifecycle change can never turn a
      // runtime-flag timeout into an empty hero.
      poster.hidden = false;
      return;
    }

    const { createLandingRuntime } = await import('./visual/landing-runtime.js');
    const runtime = createLandingRuntime({
      container: document.querySelector('#multifractal-bg'),
      poster,
      sections: [...document.querySelectorAll('[data-visual-phase]')],
      capabilities,
    });
    await runtime.start();
    addEventListener('pagehide', () => runtime.dispose(), { once: true });
  }, { timeout: 1200 });
}
